import { useEffect } from 'react';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { BodyL, LabelS } from '@sorare/core/src/atoms/typography';
import {
  GqlFilterTitle,
  useBackendRangeSlider,
} from '@sorare/core/src/contexts/graphql-search';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useRangeSlider } from '@sorare/core/src/hooks/useRangeSlider';
import type { Filter, FilterWidget } from '@sorare/core/src/lib/filters';
import { FLOOR_PRICES_FILTERS } from '@sorare/core/src/lib/filters';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';
import { floorPricesAttributes } from '@sorare/core/src/lib/scouting-center';
import { powerAlgorithm } from '@sorare/core/src/lib/slider';

import { useFormatFloorPricesFilters } from 'hooks/filters/useFormatFloorPricesFilters';
import { BackendRangeSlider, RangeSlider } from 'search/RangeSlider';
import { makeRangeFilter } from 'searchCards/RangeSlider';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

const refinePriceAlgorithm = powerAlgorithm({ power: 20 });

const REFINE_PRICE_MAX_EUR = 20000000;

const CurrencySymbol = styled(BodyL)`
  align-self: center;
`;
const EthSymbol = styled(LabelS)`
  align-self: flex-end;
`;

const FloorPricesRangeSlider = ({ filter }: { filter: Filter }) => {
  const { statsManager } = useManagePlayerSearchView();
  const currentPriceFilter = floorPricesAttributes(
    statsManager[PlayerSearchViewAttributes.RARITY],
    statsManager[PlayerSearchViewAttributes.SEASONALITY]
  );
  const visible = currentPriceFilter === filter.attribute;

  const { sliderToPrice, priceToSlider } = useFormatFloorPricesFilters();
  const { currency, fiatCurrencySymbol, cryptocurrencySymbol } =
    useCurrentUserCurrencies();
  const { refine } = useRangeSlider({
    attribute: filter.attribute,
  });
  useEffect(() => {
    if (!visible) refine([undefined, undefined]);
  }, [refine, visible]);

  return (
    <RangeSlider
      attribute={filter.attribute}
      algorithm={refinePriceAlgorithm}
      fromInput={priceToSlider}
      toInput={sliderToPrice}
      maxForced={Number(REFINE_PRICE_MAX_EUR)}
      greaterThanMode
      icon={
        currency === Currency.FIAT ? (
          <CurrencySymbol bold color="var(--c-white)">
            {fiatCurrencySymbol}
          </CurrencySymbol>
        ) : (
          <EthSymbol bold color="var(--c-white)">
            {cryptocurrencySymbol}
          </EthSymbol>
        )
      }
      visible={visible}
      disabled={!visible}
    />
  );
};

const BackendFloorPricesRangeSlider = ({
  filter,
}: {
  filter: Filter & { urlAttribute: string };
}) => {
  const { statsManager } = useManagePlayerSearchView();
  const currentPriceFilter = floorPricesAttributes(
    statsManager[PlayerSearchViewAttributes.RARITY],
    statsManager[PlayerSearchViewAttributes.SEASONALITY]
  );

  const visible = currentPriceFilter === filter.attribute;

  const { sliderToPrice, priceToSlider } = useFormatFloorPricesFilters();
  const { currency, fiatCurrencySymbol, cryptocurrencySymbol } =
    useCurrentUserCurrencies();

  const { refine, start } = useBackendRangeSlider({
    attribute: filter.urlAttribute,
  });

  useEffect(() => {
    if (!visible && start.filter(e => e !== undefined).length)
      refine([undefined, undefined]);
  }, [refine, visible, start]);

  return (
    <BackendRangeSlider
      attribute={filter.urlAttribute}
      algorithm={refinePriceAlgorithm}
      fromInput={priceToSlider}
      toInput={sliderToPrice}
      maxForced={Number(REFINE_PRICE_MAX_EUR)}
      greaterThanMode
      icon={
        currency === Currency.FIAT ? (
          <CurrencySymbol bold color="var(--c-white)">
            {fiatCurrencySymbol}
          </CurrencySymbol>
        ) : (
          <EthSymbol bold color="var(--c-white)">
            {cryptocurrencySymbol}
          </EthSymbol>
        )
      }
      visible={visible}
      disabled={!visible}
    />
  );
};

export const RefineFloorPrices = (hideTitle: boolean): FilterWidget[] =>
  Object.values(FLOOR_PRICES_FILTERS).map((filter: Filter) => {
    return makeRangeFilter(
      () => <FloorPricesRangeSlider filter={filter} />,
      filter,
      {
        startsOpen: true,
        hideTitle,
      }
    );
  });

export const BackendRefineFloorPrices = (hideTitle: boolean): FilterWidget[] =>
  Object.values(FLOOR_PRICES_FILTERS).map(filter => {
    return {
      key: filter.attribute,
      type: 'range',
      attribute: filter.attribute,
      Component: () => <BackendFloorPricesRangeSlider filter={filter} />,
      title: !hideTitle && (
        <GqlFilterTitle name={filter.urlAttribute} title={filter.title} />
      ),
      accordionOptions: { startsOpen: true },
    };
  });
